import { surveyStore } from '../stores/surveyStore';
import { browser } from '$app/environment';

export class GeolocationService {
  /**
   * Get the user's geolocation using Google Geolocation API
   * This uses the browser's IP and nearby WiFi networks information
   * @param options - Options for geolocation request
   * @param options.failSilently - If true, don't log warnings for runtime errors (default: false)
   * @param options.debug - If true, log detailed debug info (default: false)
   * @returns Promise<void> - nothing is returned, location is stored in survey store
   */
  async getGoogleGeolocation(options: { failSilently?: boolean; debug?: boolean } = {}): Promise<void> {
    const { failSilently = false, debug = false } = options;
    
    if (!browser) return;
    
    try {
      // Get the API key from env var (will be undefined in development)
      const apiKey = import.meta.env.VITE_GOOGLE_GEOLOCATION_API_KEY;
      
      if (!apiKey) {
        if (debug) console.log('[GeolocationService] No Google Geolocation API key found');
        // Always throw for missing API key regardless of failSilently
        console.error('[GeolocationService] Missing API key: VITE_GOOGLE_GEOLOCATION_API_KEY is not defined in environment variables');
        throw new Error('No Google Geolocation API key found in environment variables');
      }
      
      if (debug) console.log('[GeolocationService] API key found, preparing request');
      
      // Prepare request data according to Google's Geolocation API
      const requestData = {
        considerIp: true, // Use IP-based location
      };
      
      if (debug) console.log('[GeolocationService] Making request to Google Geolocation API');
      
      // Make API request
      const response = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        if (debug) console.log('[GeolocationService] Google API response not OK:', response.status);
        throw new Error(`Failed to get location from Google Geolocation API: ${response.status}`);
      }
      
      const data = await response.json();
      if (debug) console.log('[GeolocationService] Received data from Google API:', data);
      
      // Store result in survey store
      if (data && data.location) {
        const geoData = {
          latitude: data.location.lat,
          longitude: data.location.lng,
          accuracy: data.accuracy,
          source: 'google_geolocation_api',
          timestamp: Date.now()
        };
        
        if (debug) console.log('[GeolocationService] Storing geolocation in survey store:', geoData);
        surveyStore.addMiddlewareData('geolocation', geoData);
      } else {
        if (debug) console.log('[GeolocationService] No location data in Google API response');
        throw new Error('No location data in Google API response');
      }
    } catch (error) {
      // For missing API key errors, always propagate regardless of failSilently
      if (error instanceof Error && error.message.includes('No Google Geolocation API key')) {
        throw error;
      }
      
      // For other errors, respect the failSilently flag
      if (debug) console.log('[GeolocationService] Error with Google Geolocation API:', error);
      if (!failSilently) {
        // Rethrow the error to be handled by caller
        throw error;
      }
    }
  }
  
  /**
   * Get the user's geolocation using Google Geolocation API
   * @param options - Options for geolocation request
   * @param options.failSilently - If true, don't log warnings for runtime errors (default: false)
   * @param options.debug - If true, log detailed debug info (default: false)
   * @returns Promise<void> - nothing is returned, location is stored in survey store
   */
  async getGeolocation(options: { failSilently?: boolean; debug?: boolean } = {}): Promise<void> {
    const { failSilently = false, debug = false } = options;
    
    if (debug) console.log('[GeolocationService] getGeolocation called with options:', options);
    
    try {
      if (debug) console.log('[GeolocationService] Using Google Geolocation API');
      // Call Google's API directly - no fallback
      await this.getGoogleGeolocation({ failSilently, debug });
    } catch (error) {
      // Always rethrow configuration errors like missing API key
      if (error instanceof Error && error.message.includes('No Google Geolocation API key')) {
        console.error('[GeolocationService] Configuration error:', error);
        throw error;
      }
      
      // For runtime errors, respect the failSilently flag
      if (debug) console.log('[GeolocationService] Error in getGeolocation:', error);
      if (!failSilently) {
        console.error('[GeolocationService] Error fetching geolocation:', error);
        throw error;
      }
    }
  }
}

// Create and export a singleton instance
export const geolocationService = new GeolocationService(); 